import React, { useEffect } from "react";
import {
  FaArrowLeft,
  FaDownload,
  FaSpinner,
  FaFilePdf,
  FaExternalLinkAlt,
  FaSyncAlt,
} from "react-icons/fa";
import { productNames } from "./constants";
import ProductResultCard from "./ProductResultCard";

export default function PdfStep({
  selectedProducts,
  productStates,
  showResults,
  resultsContainerRef,
  onGeneratePDF,
  loading,
  previewUrl,
  previewFileName,
  previewLoading,
  onCreatePreview,
  onBack,
}) {
  useEffect(() => {
    if (showResults && resultsContainerRef?.current) {
      resultsContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showResults, resultsContainerRef]);

  const renderEmptyState = () => (
    <div className="p-8 rounded-2xl border border-dashed border-gray-300 bg-gray-50 text-center">
      <p className="text-sm text-gray-500">
        Henüz hesaplama yapılmadı. PDF oluşturmak için önce ürünlerin
        hesaplamasını tamamlayın.
      </p>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-sm hover:shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
          <span>Hesaplama Adımına Dön</span>
        </button>
      )}
    </div>
  );

  // Önizleme ile indirilen dosya aynı blob'tur — ekranda görülen tasarım
  // birebir indirilir.
  const renderPreview = () => (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-2 bg-red-50 border border-red-100 rounded-lg shrink-0">
            <FaFilePdf className="w-4 h-4 text-red-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Teklif Önizlemesi
            </p>
            <p className="text-xs text-gray-500 truncate">
              {previewFileName || "teklif.pdf"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCreatePreview}
            disabled={previewLoading}
            className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {previewLoading ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FaSyncAlt className="w-3.5 h-3.5" />
            )}
            <span>Yenile</span>
          </button>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors no-underline"
          >
            <FaExternalLinkAlt className="w-3.5 h-3.5" />
            <span>Yeni Sekmede Aç</span>
          </a>
        </div>
      </div>

      <div className="bg-gray-100">
        <iframe
          src={previewUrl}
          title="Teklif PDF önizlemesi"
          className="w-full h-[70vh] min-h-[420px] border-0 block"
        />
      </div>

      <p className="px-4 py-2.5 text-xs text-gray-500 border-t border-gray-200">
        Önizleme görüntülenemiyorsa (bazı mobil tarayıcılar PDF gömmeyi
        desteklemez) "Yeni Sekmede Aç" veya "PDF İndir" kullanın.
      </p>
    </div>
  );

  const renderPreviewPlaceholder = () => (
    <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 text-center">
      <div className="inline-flex p-3 bg-red-50 border border-red-100 rounded-xl">
        <FaFilePdf className="w-6 h-6 text-red-500" />
      </div>
      <p className="mt-3 text-sm font-semibold text-gray-900">
        Teklif önizlemesi
      </p>
      <p className="mt-1 text-sm text-gray-500">
        Belgeyi indirmeden önce burada görüntüleyebilirsiniz. Önizleme ile
        indirilen dosya birebir aynıdır.
      </p>
      <button
        type="button"
        onClick={onCreatePreview}
        disabled={previewLoading}
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-sm hover:shadow-md hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
      >
        {previewLoading ? (
          <FaSpinner className="w-4 h-4 animate-spin" />
        ) : (
          <FaFilePdf className="w-4 h-4" />
        )}
        <span>
          {previewLoading ? "Önizleme Hazırlanıyor..." : "Önizlemeyi Oluştur"}
        </span>
      </button>
    </div>
  );

  return (
    <div className="space-y-5">
      {!showResults || selectedProducts.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div ref={resultsContainerRef} className="grid grid-cols-1 gap-4">
            {selectedProducts.map((product) => (
              <ProductResultCard
                key={product}
                product={product}
                productState={productStates[product] || {}}
                productName={productNames[product]}
              />
            ))}
          </div>

          {previewUrl ? renderPreview() : renderPreviewPlaceholder()}

          <div className="flex flex-wrap items-center justify-between gap-3">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft className="w-3.5 h-3.5" />
                <span>Hesaplamaya Dön</span>
              </button>
            ) : (
              <span />
            )}

            <button
              type="button"
              onClick={onGeneratePDF}
              disabled={loading || previewLoading}
              className="flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-sm hover:shadow-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaDownload className="w-4 h-4" />
              )}
              <span>{loading ? "PDF Hazırlanıyor..." : "PDF İndir"}</span>
            </button>
          </div>

          {loading && (
            <p className="text-center text-sm text-gray-500">
              PDF hazır olduğunda indirme otomatik başlayacak. Lütfen sayfayı
              kapatmayın.
            </p>
          )}
        </>
      )}
    </div>
  );
}
